import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import { filter, map } from 'rxjs';

import { matchesToolSegment } from '../../../shared/utils/tool-path.util';
import { toStarkIcon } from '../../config';
import type { RouteMetadata } from '../../interfaces/navigation.interface';
import { ToolRegistryService } from '../firestore/tool-registry.service';

const DEFAULT_ROUTE_METADATA: RouteMetadata = {
  id: 'dashboard',
  name: 'Dashboard',
  icon: 'home',
};

const ADMIN_ROUTE_METADATA: Record<string, RouteMetadata> = {
  'admin/tools': { id: 'admin-tools', name: 'Tool Management', icon: 'wrench' },
  'admin/categories': { id: 'admin-categories', name: 'Category Management', icon: 'folder' },
  'admin/clients': { id: 'admin-clients', name: 'Client Control', icon: 'building-2' },
  'admin/toolbelt-schemas': {
    id: 'admin-toolbelt-schemas',
    name: 'Toolbelt Schema Management',
    icon: 'file-code',
  },
  'admin/toolbelt-schemas/new': {
    id: 'admin-toolbelt-schema-new',
    name: 'Nuevo Schema',
    icon: 'file-code',
  },
  'admin/schema-change-requests': {
    id: 'admin-schema-change-requests',
    name: 'Solicitudes de cambio',
    icon: 'git-branch',
  },
};

@Injectable({ providedIn: 'root' })
export class RouteMetadataService {
  private readonly router = inject(Router);
  private readonly toolRegistry = inject(ToolRegistryService);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url),
    ),
    { initialValue: this.router.url },
  );

  getFeatureName(): string {
    return this.currentMetadata().name;
  }

  getFeatureIcon(): string {
    return this.currentMetadata().icon;
  }

  getFeatureId(): string {
    return this.currentMetadata().id;
  }

  isRouteAvailable(routePath: string): boolean {
    const normalized = this.normalizePath(routePath);
    if (normalized in ADMIN_ROUTE_METADATA) {
      return true;
    }

    const [categoryCode, toolId] = normalized.split('/');
    if (!categoryCode || !toolId) {
      return false;
    }

    return this.toolRegistry.categories().some(
      category => category.id === categoryCode && category.tools.some(tool => matchesToolSegment(tool, toolId))
    );
  }

  private currentMetadata(): RouteMetadata {
    const path = this.normalizePath(this.currentUrl());
    const adminMetadata = ADMIN_ROUTE_METADATA[path];
    if (adminMetadata) {
      return adminMetadata;
    }

    const [, toolId] = path.split('/');
    if (!toolId) {
      return DEFAULT_ROUTE_METADATA;
    }

    for (const category of this.toolRegistry.categories()) {
      const tool = category.tools.find(item => matchesToolSegment(item, toolId));
      if (tool) {
        return {
          id: tool.id,
          name: tool.name,
          icon: toStarkIcon(tool.icon),
        };
      }
    }

    return DEFAULT_ROUTE_METADATA;
  }

  private normalizePath(path: string): string {
    return path.split('?')[0].split('#')[0].split('/').filter(Boolean).join('/');
  }
}
